/**
 * ActionBar Component
 * 
 * Context-aware action buttons that appear based on current tasks.
 * Implements progressive disclosure - show only relevant actions.
 * 
 * Features:
 * - Auto-detects pending tasks from useTaskGeneration
 * - Shows max 4 most important actions
 * - Prioritizes by task priority (high > medium > low)
 * - One-click navigation to actions
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md - Fix 3
 */

import { useTaskGeneration } from '@/hooks/useTaskGeneration';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface ActionBarProps {
  maxActions?: number;
  className?: string;
}

export const ActionBar = ({ maxActions = 4, className = '' }: ActionBarProps) => {
  const { tasks, isLoading } = useTaskGeneration({ maxTasks: maxActions });

  // Only show pending tasks
  const pendingTasks = tasks.filter(t => t.status === 'pending');

  if (isLoading) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  // Don't show if no tasks
  if (pendingTasks.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="text-xs text-brand-text-secondary font-medium">
        Quick Actions:
      </span>
      {pendingTasks.slice(0, maxActions).map((task) => {
        // Determine button variant based on priority
        const variant = task.priority === 'high' ? 'default' : 'outline';
        
        // Determine color classes based on priority
        const priorityClasses = {
          high: 'border-validation-critical hover:bg-validation-critical hover:text-white',
          medium: 'border-validation-high hover:bg-validation-high hover:text-white',
          low: 'border-validation-low hover:bg-validation-low hover:text-white',
        };

        return (
          <Button
            key={task.id}
            size="sm"
            variant={variant}
            onClick={task.action}
            className={`
              flex items-center gap-2 rounded-lg transition-all
              ${task.priority === 'high' ? 'bg-brand-accent hover:bg-brand-accent/90' : ''}
              ${task.priority !== 'high' ? priorityClasses[task.priority] : ''}
            `}
          >
            <span className="text-lg">{task.icon}</span>
            <span className="font-medium">{task.title}</span>
            {task.count && task.count > 0 && (
              <Badge 
                variant="secondary" 
                className="ml-1 text-[10px] h-5 min-w-[20px] justify-center"
              >
                {task.count}
              </Badge>
            )}
          </Button>
        );
      })}
    </div>
  );
};
